import {
	type InstrumentInsightsItem,
	type MetricsList,
	type ReportHorizon,
	CommentaryTemplateControllerApiFactory,
} from "@eu.mdotm.api/sphere";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Badge,
	Collapsible,
	Column,
	Controller,
	DefaultCollapsibleContent,
	Dialog,
	DialogFooter,
	DialogHeader,
	Icon,
	Row,
	Slider,
	Text,
} from "@mdotm/shadcn/legacy/components";
import { Checkbox, NumberInput, TextArea, Button } from "@mdotm/shadcn/mdotm/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { nullary } from "@mdotm/shadcn/legacy/utils";
import type { MaybePromise } from "@mdotm/utils/promise";
import { ForEach } from "@mdotm/utils/react-extra";
import { type ReactNode, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useApiGen } from "$root/api/hooks";
import RoundedButton from "$root/components/buttons/RoundedButton";
import { LeavePromptBase } from "$root/components/LeavePrompt";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { FormController } from "$root/third-party-integrations/react-hook-form";
import { useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import { FormFields } from "$root/ui-lib/form/FormFields";
import { customObjectEntriesFn, customObjectValuesFn, qualifier } from "$root/utils";
import {
	type AllowedModules,
	defaultSelectableHorizonRealisedMetrics,
	type StableConfiguration,
	selectableHorizonRealisedMetricsOptions,
	selectablePerformanceAnalysisHorizon,
} from "./common";
import { AvailableSoonBadge, DataSourceFormSection, DataSourceSection, dataSourceValidation } from "./data-source";

export function SideHeaderContent(props: {
	title: string;
	description: string;
	onCancel?(): void;
	onSubmit(): MaybePromise<void>;
}): React.JSX.Element {
	const { t } = useTranslation();
	return (
		<div className={`border-b border-solid border-b-[color:var(--palette-N100)] `}>
			<div className="flex items-center px-4 pt-5 pb-2">
				<Text type="Body/XL/Bold" as="p" className="grow h-fit line-clamp-2">
					{props.title}
				</Text>
				<div className="flex justify-end space-x-4 bg-white items-center">
					<Button
						size="small"
						palette="tertiary"
						onClick={props.onCancel}
						data-qualifier={qualifier.storyfolio.component.sideHeaderContent.cancel}
					>
						{t("BUTTON.CANCEL")}
					</Button>
					<Button
						size="small"
						palette="primary"
						onClickAsync={props.onSubmit}
						data-qualifier={qualifier.storyfolio.component.sideHeaderContent.submit}
					>
						{t("BUTTON.APPLY")}
					</Button>
				</div>
			</div>
			<Text type="Body/M/Book" className="px-4 pb-4" color={themeCSSVars.palette_N700} as="p">
				{props.description}
			</Text>
		</div>
	);
}

export function SideBarContent(props: {
	title: string;
	description: string;
	onSubmit(): MaybePromise<void>;
	onCancel(): void;
	children: ReactNode;
}): React.JSX.Element {
	// bg-[color:var(--palette-N20)]
	return (
		<div className="grow bg-white overflow-y-auto pb-6">
			<SideHeaderContent
				title={props.title}
				description={props.description}
				onSubmit={props.onSubmit}
				onCancel={props.onCancel}
			/>
			<div className={`px-4 pt-4 border-t border-solid border-t-[color:var(--palette-N100)]`}>{props.children}</div>
		</div>
	);
}

type CommonModuleProps<K extends AllowedModules> = {
	onCancel(): void;
	module: StableConfiguration[K];
	onChange(module: StableConfiguration[K]): void;
};
const activerRoundedButtonClass = `!bg-[var(--palette-P100)] !text-[var(--palette-P600)]`;
export function ModuleStylingBlock(props: {
	description: string | undefined;
	onChange(description?: string): void;
}): React.JSX.Element {
	const commentaryTemplateApi = useApiGen(CommentaryTemplateControllerApiFactory);
	const { isFetching, data } = useQueryNoRefetch(["loadModuleStyling"], {
		queryFn: () => axiosExtract(commentaryTemplateApi.getModuleStyling()),
	});
	return (
		<>
			<div className="flex gap-2 items-center mb-2">
				<Text type="Body/L/Bold" as="p">
					Module Styling
				</Text>
				<Badge size="x-small" color="white" backgroundColor={themeCSSVars.palette_P500}>
					Beta
				</Badge>
			</div>

			<div className="flex space-x-2 mb-2">
				<RoundedButton
					disabled={isFetching || data === undefined}
					onClick={() => props.onChange(props.description === data?.max40Words ? undefined : data?.max40Words)}
					className={{
						[activerRoundedButtonClass]: props.description === data?.max40Words,
					}}
					data-qualifier={qualifier.storyfolio.component.moduleStyling.summarize}
				>
					Summarize
				</RoundedButton>
				<RoundedButton
					disabled={isFetching || data === undefined}
					onClick={() => props.onChange(props.description === data?.max80Words ? undefined : data?.max80Words)}
					className={{
						[activerRoundedButtonClass]: props.description === data?.max80Words,
					}}
					data-qualifier={qualifier.storyfolio.component.moduleStyling.extended}
				>
					Extended
				</RoundedButton>
				<RoundedButton
					disabled={isFetching || data === undefined}
					onClick={() => props.onChange(props.description === data?.tableFormat ? undefined : data?.tableFormat)}
					className={{
						[activerRoundedButtonClass]: props.description === data?.tableFormat,
					}}
					data-qualifier={qualifier.storyfolio.component.moduleStyling.tableFormat}
				>
					Table format
				</RoundedButton>
				<RoundedButton
					onClick={() => props.onChange(props.description === "" ? undefined : "")}
					className={{
						[activerRoundedButtonClass]:
							props.description === undefined || data === undefined
								? false
								: props.description === "" || customObjectValuesFn(data).every((x) => x !== props.description),
					}}
					data-qualifier={qualifier.storyfolio.component.moduleStyling.customPrompt}
				>
					Custom prompt
				</RoundedButton>
			</div>
			<Controller value={props.description} onChange={props.onChange}>
				{({ onChange, onCommit, value }) => (
					<TextArea
						rows={4}
						className={{
							"[&>textarea]:resize-none overflow-y-auto": true,
						}}
						value={value}
						onChangeText={onChange}
						onBlur={nullary(onCommit)}
						data-qualifier={qualifier.storyfolio.component.moduleStyling.content}
					/>
				)}
			</Controller>
		</>
	);
}

function LeavePromptModule(props: { isDirty: boolean; children: ReactNode; onCancel?(): void }) {
	const { t } = useTranslation();
	return (
		<>
			<LeavePromptBase when={props.isDirty} pathToNotBlock={["/login"]}>
				{({ onCancelNavigation, onContinueNavigation, showPrompt }) => (
					<Dialog
						show={showPrompt}
						onClose={onCancelNavigation}
						header={
							<DialogHeader
								icon={<Icon icon="Icon-full-alert" color={themeCSSVars.MessageSeverity_warning} size={22} />}
							>
								You are about to leave the module customization
							</DialogHeader>
						}
						footer={
							<DialogFooter
								primaryAction={
									<Button
										onClick={() => {
											onContinueNavigation();
											props.onCancel?.();
										}}
										palette="primary"
									>
										{t("LEAVE")}
									</Button>
								}
								neutralAction={
									<Button onClick={onCancelNavigation} palette="tertiary">
										{t("STAY")}
									</Button>
								}
							/>
						}
					>
						Are your sure want to leave the module? All unsaved changes will be lost.
					</Dialog>
				)}
			</LeavePromptBase>
			{props.children}
		</>
	);
}

export function MarketOulookModule(props: CommonModuleProps<"marketOutlook">): React.JSX.Element {
	const { handleSubmit, formState, watch, setValue, reset } = useForm({
		defaultValues: props.module,
	});

	return (
		<LeavePromptModule
			isDirty={formState.isDirty}
			onCancel={() => {
				props.onCancel();
				reset();
			}}
		>
			<SideBarContent
				title="#Market view"
				description="An overview of the market view associated with the portfolio."
				onSubmit={handleSubmit((params) =>
					props.onChange({
						...params,
						enabled: true,
					}),
				)}
				onCancel={props.onCancel}
			>
				<ModuleStylingBlock
					description={watch("moduleStyling")}
					onChange={(modules) => setValue("moduleStyling", modules, { shouldDirty: true })}
				/>
			</SideBarContent>
		</LeavePromptModule>
	);
}

export function PortfolioDynamicModule(props: CommonModuleProps<"dynamicsBlock">): React.JSX.Element {
	const { control, handleSubmit, formState, watch, setValue, reset } = useForm({
		defaultValues: props.module,
		resolver: zodResolver(dataSourceValidation),
	});

	return (
		<LeavePromptModule
			isDirty={formState.isDirty}
			onCancel={() => {
				props.onCancel();
				reset();
			}}
		>
			<SideBarContent
				title="#Portfolio dynamics"
				description="Analysis of portfolio allocation changes by comparing the two most recent allocations and assessing alignment with the market view."
				onSubmit={handleSubmit((params) =>
					props.onChange({
						...params,
						enabled: true,
					}),
				)}
				onCancel={props.onCancel}
			>
				<DataSourceFormSection
					control={control}
					riskModelDescription="Portfolio allocation shifts analyzed using Sphere’s risk model, providing insights into macro and micro asset class breakdowns and their alignment with the market view."
					customClassificationDescription="Portfolio allocation shifts analyzed based on user-defined categories, delivering insights tailored to tags and their alignment with the market view."
				/>
				<Divider />
				<Text type="Body/L/Bold" className="mb-4" as="p">
					Customize content
				</Text>

				<FormFields.Checkbox
					control={control}
					formState={formState}
					name="enableMarketViewCoherence"
					className="mb-1"
					data-qualifier={qualifier.storyfolio.modules.portfolioDynamics.enableMarketViewCoherence}
				>
					Market view coherence check
				</FormFields.Checkbox>

				<Text type="Body/M/Book" color={themeCSSVars.palette_N500} as="p" className="mt-4">
					{watch("commentaryModelType") === "TAG"
						? "Relevance threshold for delta tag"
						: "Relevance threshold for delta Micro Asset Class"}
				</Text>
				{/* <FormFields.Checkbox control={control} formState={formState} name="mainInstrument" className="mb-1">
					Relevance threshold for for delta Micro Asset Class
				</FormFields.Checkbox> */}
				<FormController
					name="relevanceThreshold"
					control={control}
					render={({ field: { ref, ...controllerProps } }) => (
						<Slider
							min={0}
							max={100}
							step={0.01}
							disabled={!watch("mainInstrument")}
							{...controllerProps}
							input={{
								icon: "Percentile",
								innerRef: ref,
							}}
							data-qualifier={qualifier.storyfolio.modules.portfolioDynamics.relevanceThreshold}
						/>
					)}
				/>
				<Divider />
				<ModuleStylingBlock
					description={watch("moduleStyling")}
					onChange={(modules) => setValue("moduleStyling", modules, { shouldDirty: true })}
				/>
			</SideBarContent>
		</LeavePromptModule>
	);
}

export function PortfolioCompositionModule(props: CommonModuleProps<"portfolioCompositionBlock">): React.JSX.Element {
	const { control, handleSubmit, formState, watch, setValue, reset } = useForm({
		defaultValues: props.module,
		resolver: zodResolver(dataSourceValidation),
	});

	return (
		<LeavePromptModule
			isDirty={formState.isDirty}
			onCancel={() => {
				props.onCancel();
				reset();
			}}
		>
			<SideBarContent
				title="#Portfolio Composition"
				description="A detailed breakdown of the current portfolio composition."
				onSubmit={handleSubmit((params) =>
					props.onChange(
						params.commentaryModelType === "TAG"
							? {
									...params,
									enabled: true,
									enableMarketViewCoherence: false,
									commentOnBenchmark: false,
								}
							: {
									...params,
									enabled: true,
								},
					),
				)}
				onCancel={props.onCancel}
			>
				<DataSourceFormSection
					control={control}
					riskModelDescription="Portfolio composition analyzed using Sphere’s risk model, providing insights into macro and micro asset class breakdowns."
					customClassificationDescription="Portfolio composition analyzed based on user-defined categories, delivering insights tailored to tags."
				/>
				<Divider />
				<Text type="Body/L/Bold" className="mb-4" as="p">
					Customize content
				</Text>
				<FormFields.Checkbox
					control={control}
					formState={formState}
					name="commentOnBenchmark"
					className="mb-1"
					// TODO(BE): remove disabled and AvailableSoonBadge once BE is ready
					disabled={watch("commentaryModelType") === "TAG"}
					data-qualifier={qualifier.storyfolio.modules.portfolioComposition.commentBenchmark}
					onChange={(newValue) => {
						if (!newValue) {
							setValue("enableMarketViewCoherence", false);
						}
					}}
				>
					Comment the benchmark
					{watch("commentaryModelType") === "TAG" && (
						<>
							&nbsp;
							<AvailableSoonBadge />
						</>
					)}
				</FormFields.Checkbox>
				{watch("commentaryModelType") === "RISK_MODEL" && (
					<FormFields.Checkbox
						control={control}
						formState={formState}
						name="enableMarketViewCoherence"
						className="mb-1 ml-6"
						disabled={watch("commentOnBenchmark") === false}
						data-qualifier={qualifier.storyfolio.modules.portfolioComposition.MarketViewCoherenceCheck}
					>
						Market view coherence check
					</FormFields.Checkbox>
				)}
				<FormFields.Checkbox
					control={control}
					formState={formState}
					name="commentConstraint"
					className="mb-1"
					data-qualifier={qualifier.storyfolio.modules.portfolioComposition.commentAssetClassConstrainnts}
				>
					{watch("commentaryModelType") === "TAG" ? "Comment tag constraints" : "Comment Asset Class constraints"}
				</FormFields.Checkbox>
				<FormFields.Checkbox
					control={control}
					formState={formState}
					name="showMainInstrumentForMacroAc"
					className="mb-1"
					data-qualifier={qualifier.storyfolio.modules.portfolioComposition.highlightMainInstrumentMacroAssetClass}
				>
					{watch("commentaryModelType") === "TAG"
						? "Highlight main instrument for tag"
						: "Highlight main instrument for macro Asset Class"}
				</FormFields.Checkbox>

				<Text type="Body/M/Book" className="mt-4" color={themeCSSVars.palette_N500} as="p">
					{watch("commentaryModelType") === "TAG"
						? "Relevance threshold for tags commented"
						: "Relevance threshold for Micro Asset Class commented"}
				</Text>
				<FormController
					name="relevanceThresholdForMicroAcCommented"
					control={control}
					render={({ field: { ref, ...controllerProps } }) => (
						<Slider
							min={0}
							max={100}
							step={0.01}
							{...controllerProps}
							input={{
								icon: "Percentile",
								innerRef: ref,
							}}
							data-qualifier={qualifier.storyfolio.modules.portfolioComposition.relevanceThresholdForMicroAcCommented}
						/>
					)}
				/>

				<Divider />
				<ModuleStylingBlock
					description={watch("moduleStyling")}
					onChange={(modules) => setValue("moduleStyling", modules, { shouldDirty: true })}
				/>
			</SideBarContent>
		</LeavePromptModule>
	);
}

/** @internal — exported for tests */
export function makeInstrumentToupleRequired(data: InstrumentInsightsItem): Required<InstrumentInsightsItem> {
	return { enabled: data.enabled ?? false, threshold: data.threshold ?? 0 };
}

export function InstumentInsightsModule(props: CommonModuleProps<"instrumentInsightsBlock">): React.JSX.Element {
	const { control, handleSubmit, formState, watch, setValue, reset } = useForm({
		defaultValues: {
			commentedInstrument: makeInstrumentToupleRequired(props.module.commentedInstrument),
			newInstrument: makeInstrumentToupleRequired(props.module.newInstrument),
			deletedInstrument: makeInstrumentToupleRequired(props.module.deletedInstrument),
			instrumentWithNegativeDeltaWeight: makeInstrumentToupleRequired(props.module.instrumentWithNegativeDeltaWeight),
			instrumentWithPositiveDeltaWeight: makeInstrumentToupleRequired(props.module.instrumentWithPositiveDeltaWeight),
			enabled: props.module.enabled,
			moduleStyling: props.module.moduleStyling,
			returnsOnSingleInstrument: props.module.returnsOnSingleInstrument,
		} satisfies Record<keyof typeof props.module, unknown>,
	});

	return (
		<LeavePromptModule
			isDirty={formState.isDirty}
			onCancel={() => {
				props.onCancel();
				reset();
			}}
		>
			<SideBarContent
				title="#Instrument insights"
				description="Key highlights of instruments bought or sold, with a focus on those that experienced significant changes."
				onSubmit={handleSubmit((params) =>
					props.onChange({
						...params,
						enabled: true,
					}),
				)}
				onCancel={props.onCancel}
			>
				<Text type="Body/L/Bold" className="mb-4" as="p">
					Customize content
				</Text>
				<div className="flex items-center space-x-2 mb-1">
					<FormFields.Checkbox
						control={control}
						formState={formState}
						name="newInstrument.enabled"
						data-qualifier={qualifier.storyfolio.modules.instrumentInsights.maxNumOfAddedInstruments}
					>
						Max n. of added instruments
					</FormFields.Checkbox>
					<FormFields.NullableNumber
						name="newInstrument.threshold"
						control={control}
						label=""
						formState={formState}
						size="x-small"
						className="w-[38px]"
						data-qualifier={qualifier.storyfolio.modules.instrumentInsights.maxNumOfAddedInstrumentsThreshold}
					/>
				</div>

				<div className="flex items-center space-x-2 mb-1">
					<FormFields.Checkbox
						control={control}
						formState={formState}
						name="deletedInstrument.enabled"
						data-qualifier={qualifier.storyfolio.modules.instrumentInsights.maxNumOfDeletedInstruments}
					>
						Max n. of deleted instruments
					</FormFields.Checkbox>
					<FormFields.NullableNumber
						name="deletedInstrument.threshold"
						control={control}
						label=""
						formState={formState}
						size="x-small"
						className="w-[38px]"
						data-qualifier={qualifier.storyfolio.modules.instrumentInsights.maxNumOfDeletedInstrumentsThreshold}
					/>
				</div>

				<div className="flex items-center space-x-2 mb-1">
					<FormFields.Checkbox
						control={control}
						formState={formState}
						name="instrumentWithPositiveDeltaWeight.enabled"
						data-qualifier={qualifier.storyfolio.modules.instrumentInsights.maxNumOfPositiveDeltaInstruments}
					>
						Max n. of instruments with positive delta weights
					</FormFields.Checkbox>
					<FormFields.NullableNumber
						name="instrumentWithPositiveDeltaWeight.threshold"
						control={control}
						label=""
						formState={formState}
						size="x-small"
						className="w-[38px]"
						data-qualifier={qualifier.storyfolio.modules.instrumentInsights.maxNumOfPositiveDeltaInstrumentsThreshold}
					/>
				</div>

				<div className="flex items-center space-x-2 mb-4">
					<FormFields.Checkbox
						control={control}
						formState={formState}
						name="instrumentWithNegativeDeltaWeight.enabled"
						data-qualifier={qualifier.storyfolio.modules.instrumentInsights.maxNumOfPositiveDeltaInstruments}
					>
						Max n. of instruments with negative delta weights
					</FormFields.Checkbox>
					<FormFields.NullableNumber
						name="instrumentWithNegativeDeltaWeight.threshold"
						control={control}
						label=""
						formState={formState}
						size="x-small"
						className="w-[38px]"
						data-qualifier={qualifier.storyfolio.modules.instrumentInsights.maxNumOfPositiveDeltaInstruments}
					/>
				</div>

				<Text type="Body/M/Bold" className="mb-4" as="p">
					Additional content
				</Text>

				<div className="flex items-center mb-4">
					<FormFields.Checkbox
						control={control}
						formState={formState}
						name="returnsOnSingleInstrument"
						data-qualifier={qualifier.storyfolio.modules.instrumentInsights.maxNumOfPositiveDeltaInstruments}
					>
						Realised 3M return
					</FormFields.Checkbox>
				</div>

				<Divider />
				<ModuleStylingBlock
					description={watch("moduleStyling")}
					onChange={(modules) => setValue("moduleStyling", modules, { shouldDirty: true })}
				/>
			</SideBarContent>
		</LeavePromptModule>
	);
}

/** @internal — exported for tests */
export function getRealizedMetricHorizon(metric: MetricsList): ReportHorizon[] {
	return metric.realisedMetrics?.flatMap((x) => (x.enabled && x.metricHorizon ? [x.metricHorizon] : [])) ?? [];
}

/** @internal — exported for tests */
export function composeRealisedMetricsFromHorizon(realisedMetric: {
	enabled: boolean;
	horizons: ReportHorizon[];
}): MetricsList {
	const realisedMetrics = defaultSelectableHorizonRealisedMetrics.map(
		(opt): Required<MetricsList>["realisedMetrics"][number] => ({
			metricHorizon: opt.metricHorizon,
			enabled: realisedMetric.horizons.includes(opt.metricHorizon),
		}),
	);
	return { realisedMetrics, enabled: realisedMetric.enabled };
}

export function PortfolioMetricsModule(props: CommonModuleProps<"metricsBlock">): React.JSX.Element {
	const { t } = useTranslation();
	const { efficiencyRatio, maxDrawDown, performances, sortino, volatility, ...rest } = props.module;
	const { control, handleSubmit, formState, watch, reset } = useForm({
		defaultValues: {
			...rest,
			efficiencyRatio: {
				enabled: efficiencyRatio.enabled ?? false,
				horizons: getRealizedMetricHorizon(efficiencyRatio),
			},
			maxDrawDown: {
				enabled: maxDrawDown.enabled ?? false,
				horizons: getRealizedMetricHorizon(maxDrawDown),
			},
			performances: {
				enabled: performances.enabled ?? false,
				horizons: getRealizedMetricHorizon(performances),
			},
			sortino: {
				enabled: sortino.enabled ?? false,
				horizons: getRealizedMetricHorizon(sortino),
			},
			volatility: {
				enabled: volatility.enabled ?? false,
				horizons: getRealizedMetricHorizon(volatility),
			},
		},
		// TODO(BE): uncomment once BE is ready
		// resolver: zodResolver(dataSourceValidation),
	});

	const remappedPortfolioMetrics = useMemo(
		() =>
			({
				RISK_CONSTRAINTS: [
					"riskBudgetOnVarVolatility",
					"riskBudgetTrackingError",
					"varVolatilityRiskConstraint",
					"trackingError",
				],
				REALISED_METRICS: ["performances", "volatility", "sortino", "maxDrawDown", "efficiencyRatio"],
				EX_ANTE_METRICS: [
					"exAnteAnnualizedReturn",
					"exAnteAnnualizedVolatility",
					"exAnteDiversificationRatio",
					"exAnteEfficiencyRatio",
					"exAnteMaxDrawDown",
					"exAnteVar",
					"exAnteVolatilityContribution",
				],
				FACTOR_EXPOSURE: ["riskFactors"],
			}) as const,
		[],
	);

	const observedMetrics = watch();
	const countOfSelectedMetrics = useMemo(() => {
		return customObjectEntriesFn(remappedPortfolioMetrics).reduce(
			(acc, item) => {
				const [group] = item;
				if (group === "REALISED_METRICS") {
					const selected = remappedPortfolioMetrics[group].reduce((counter, el) => {
						const realisedMetric = observedMetrics[el];
						if (!realisedMetric.enabled) {
							return counter;
						}

						return counter + realisedMetric.horizons.length;
					}, 0);
					acc[group] = selected;
					return acc;
				}
				const selected = remappedPortfolioMetrics[group].reduce(
					(counter, el) => counter + (observedMetrics[el] ? 1 : 0),
					0,
				);
				acc[group] = selected;
				return acc;
			},
			{
				RISK_CONSTRAINTS: 0,
				EX_ANTE_METRICS: 0,
				FACTOR_EXPOSURE: 0,
				REALISED_METRICS: 0,
			},
		);
	}, [remappedPortfolioMetrics, observedMetrics]);

	return (
		<LeavePromptModule
			isDirty={formState.isDirty}
			onCancel={() => {
				props.onCancel();
				reset();
			}}
		>
			<SideBarContent
				title="#Portfolio metrics"
				description="A summary of the key portfolio metrics."
				onSubmit={handleSubmit((params) => {
					const { efficiencyRatio, maxDrawDown, performances, sortino, volatility, ...rest } = params;
					props.onChange({
						...rest,
						enabled: true,
						efficiencyRatio: composeRealisedMetricsFromHorizon(efficiencyRatio),
						maxDrawDown: composeRealisedMetricsFromHorizon(maxDrawDown),
						performances: composeRealisedMetricsFromHorizon(performances),
						sortino: composeRealisedMetricsFromHorizon(sortino),
						volatility: composeRealisedMetricsFromHorizon(volatility),
					});
				})}
				onCancel={props.onCancel}
			>
				<DataSourceFormSection
					control={control}
					riskModelDescription="Analysis of key portfolio metrics using Sphere’s risk model"
					customClassificationDescription="Analysis of key portfolio metrics using user-defined classifications."
					isClassificationSelectionComingSoon // TODO(BE): remove once BE is ready
				/>
				<Divider />
				<Text type="Body/L/Bold" className="mb-4" as="p">
					Customize content
				</Text>

				<Text type="Body/M/Bold" className="my-2" as="p">
					Select the elements you want to display
				</Text>

				<div className="flex flex-col gap-2 mb-2">
					<ForEach collection={customObjectEntriesFn(remappedPortfolioMetrics)}>
						{([group, items]) => {
							if (group === "REALISED_METRICS") {
								return (
									<Collapsible
										header={`${t(`PORTFOLIO_METRICS_MODULE.${group}`)} ${
											countOfSelectedMetrics[group] ? `(${countOfSelectedMetrics[group]})` : ""
										}`}
									>
										{({ expand }) => (
											<DefaultCollapsibleContent expand={expand}>
												<ForEach collection={remappedPortfolioMetrics[group]}>
													{(key) => (
														<div>
															<div className="flex justify-between items-center mb-2">
																<FormFields.Checkbox
																	control={control}
																	formState={formState}
																	name={`${key}.enabled`}
																	className="mb-1"
																>
																	{t(`PORTFOLIO_METRICS_MODULE.METRICS.${key}`)}
																</FormFields.Checkbox>
																<FormFields.Select
																	disabled={!watch(`${key}.enabled`)}
																	control={control}
																	formState={formState}
																	name={`${key}.horizons`}
																	multi
																	label=""
																	options={selectableHorizonRealisedMetricsOptions.map((horizon) => ({
																		...horizon,
																		label: t(`FORECAST_HORIZON.${horizon.label}`),
																	}))}
																	placeholder="Select time horizon"
																	size="small"
																	className="w-[260px]"
																/>
															</div>
														</div>
													)}
												</ForEach>
											</DefaultCollapsibleContent>
										)}
									</Collapsible>
								);
							}
							return (
								<Collapsible
									header={`${t(`PORTFOLIO_METRICS_MODULE.${group}`)} ${
										countOfSelectedMetrics[group] ? `(${countOfSelectedMetrics[group]})` : ""
									}`}
								>
									{({ expand }) => (
										<DefaultCollapsibleContent expand={expand}>
											<ForEach collection={items}>
												{(key) => (
													<div className="mb-1">
														{key === "exAnteVolatilityContribution" && watch("commentaryModelType") === "TAG" ? (
															// TODO(BE): remove once BE is ready
															<Checkbox name={key} checked={false} disabled>
																{t(`PORTFOLIO_METRICS_MODULE.METRICS.${key}`)}
																&nbsp;
																<AvailableSoonBadge />
															</Checkbox>
														) : (
															<FormFields.Checkbox control={control} formState={formState} name={key}>
																{t(`PORTFOLIO_METRICS_MODULE.METRICS.${key}`)}
															</FormFields.Checkbox>
														)}
													</div>
												)}
											</ForEach>
										</DefaultCollapsibleContent>
									)}
								</Collapsible>
							);
						}}
					</ForEach>
				</div>
				<Text type="Body/L/Bold" className="mb-2" as="p">
					Compare
				</Text>
				<FormFields.Checkbox control={control} formState={formState} name="compareWithBenchmark" className="mb-1">
					Show benchmark
				</FormFields.Checkbox>
				{/* <Divider />
						<ModuleStylingBlock
							description={watch("moduleStyling")}
							onChange={(modules) => setValue("moduleStyling", modules, { shouldDirty: true })}
						/> */}
			</SideBarContent>
		</LeavePromptModule>
	);
}

export function PerformanceAnalysisModule(props: CommonModuleProps<"performanceAnalysisBlock">): React.JSX.Element {
	const { t } = useTranslation();
	const { control, handleSubmit, formState, watch, setValue, reset } = useForm({
		defaultValues: props.module,
	});
	const horizonOptions = useMemo(
		() =>
			selectablePerformanceAnalysisHorizon.map((horizon) => ({
				label: t(`FORECAST_HORIZON.${horizon.label}`),
				value: horizon.value,
			})),
		[t],
	);

	return (
		<LeavePromptModule
			isDirty={formState.isDirty}
			onCancel={() => {
				props.onCancel();
				reset();
			}}
		>
			<SideBarContent
				title="#Performance analysis"
				description="An evaluation of portfolio performance, highlighting key contributors and the impact of allocation and selection decisions."
				onSubmit={handleSubmit((params) =>
					props.onChange({
						...params,
						enabled: true,
					}),
				)}
				onCancel={props.onCancel}
			>
				{/* TODO(BE): replace with DataSourceFormSection once BE is ready */}
				<DataSourceSection
					commentaryModelType="RISK_MODEL"
					onChangeDataSource={() => undefined}
					tagUUID={null}
					onChangeClassificationUuid={() => undefined}
					riskModelDescription="Portfolio performance analyzed using Sphere’s risk model, with contribution and attribution assessed based on Sphere’s asset class breakdown."
					isCustomComingSoon
				/>
				<Divider />
				<Text type="Body/L/Bold" className="mb-4" as="p">
					Customize content
				</Text>
				<Column gap={8}>
					<FormFields.Select
						label="Performance"
						control={control}
						formState={formState}
						name="performanceHorizon"
						data-qualifier={qualifier.storyfolio.modules.performanceAnalysis.horizon}
						options={horizonOptions}
					/>
					<Text as="div" type="Body/M/Bold" color={themeCSSVars.palette_N700}>
						Add details
					</Text>
					<FormFields.Checkbox
						control={control}
						formState={formState}
						name="showAttribution"
						data-qualifier={qualifier.storyfolio.modules.performanceAnalysis.showAttribution}
					>
						Attribution
					</FormFields.Checkbox>
					<div className="border-b" style={{ borderColor: themeCSSVars.palette_N100 }} />
					<FormFields.Checkbox
						control={control}
						formState={formState}
						name="showContribution"
						data-qualifier={qualifier.storyfolio.modules.performanceAnalysis.showContribution}
						onChange={(x) => {
							if (x) {
								setValue("positiveContributionContributors.enabled", true);
								setValue("negativeContributionContributors.enabled", true);
							}
						}}
					>
						Contribution
					</FormFields.Checkbox>
					<Column className="pl-4 max-w-[300px]" gap={12}>
						<Row alignItems="center" justifyContent="space-between">
							<FormFields.Checkbox
								control={control}
								formState={formState}
								name="positiveContributionContributors.enabled"
								disabled={!watch("showContribution")}
								data-qualifier={qualifier.storyfolio.modules.performanceAnalysis.showPositiveContributors}
								onChange={(x) => {
									if (!x && !watch("negativeContributionContributors.enabled")) {
										setValue("showContribution", false);
									}
								}}
							>
								Show n° of positive contributors
							</FormFields.Checkbox>
							<FormController
								control={control}
								name="positiveContributionContributors.number"
								render={({ field: { ref, ...controllerProps } }) => (
									<NumberInput
										min={1}
										max={1000}
										size="x-small"
										innerRef={ref}
										className="w-12"
										{...controllerProps}
										value={controllerProps.value ?? null}
										disabled={!watch("showContribution")}
										data-qualifier={qualifier.storyfolio.modules.performanceAnalysis.showNumberOfPositiveContributors}
									/>
								)}
							/>
						</Row>
						<Row alignItems="center" justifyContent="space-between">
							<FormFields.Checkbox
								control={control}
								formState={formState}
								name="negativeContributionContributors.enabled"
								disabled={!watch("showContribution")}
								data-qualifier={qualifier.storyfolio.modules.performanceAnalysis.showNegativeContributors}
								onChange={(x) => {
									if (!x && !watch("positiveContributionContributors.enabled")) {
										setValue("showContribution", false);
									}
								}}
							>
								Show n° of negative contributors
							</FormFields.Checkbox>
							<FormController
								control={control}
								name="negativeContributionContributors.number"
								render={({ field: { ref, ...controllerProps } }) => (
									<NumberInput
										min={1}
										max={1000}
										size="x-small"
										innerRef={ref}
										className="w-12"
										{...controllerProps}
										value={controllerProps.value ?? null}
										disabled={!watch("showContribution")}
										data-qualifier={qualifier.storyfolio.modules.performanceAnalysis.showNumberOfNegativeContributors}
									/>
								)}
							/>
						</Row>
					</Column>
					<div className="border-b" style={{ borderColor: themeCSSVars.palette_N100 }} />
					<Text as="div" type="Body/M/Bold" color={themeCSSVars.palette_N700}>
						Attribution and contribution type of comment
					</Text>
					<FormFields.Checkbox
						control={control}
						formState={formState}
						name="qualitativeOnly"
						data-qualifier={qualifier.storyfolio.modules.performanceAnalysis.purelyQualitative}
					>
						Purely qualitative
					</FormFields.Checkbox>
					<div className={`mt-4 pt-4 -mx-4 border-t border-solid border-t-[color:var(--palette-N100)]`}>
						<div className="px-4">
							<ModuleStylingBlock
								description={watch("moduleStyling")}
								onChange={(modules) => setValue("moduleStyling", modules, { shouldDirty: true })}
							/>
						</div>
					</div>
				</Column>
			</SideBarContent>
		</LeavePromptModule>
	);
}

function Divider() {
	return <div className={`my-4 -mx-4 border-t border-solid border-t-[color:var(--palette-N100)]`} />;
}
